import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { enrollQueueOpenRequest } from '../../../lib/enroll-queue-open-request';

/** The subset of a queue item this hook needs to find and target the newest one. */
export interface OpenRequestTarget {
  id: number;
  contact_id: string;
}

/**
 * How long to keep watching for the item an open request was made FOR.
 *
 * The request fires the moment the approval POST resolves, which is before the
 * queue query has refetched — so the item usually is not in `items` yet. Six
 * seconds covers the invalidate-plus-refetch round trip without leaving a watcher
 * armed long enough to grab an item the rep added by hand afterwards.
 */
const WATCH_MS = 12000;

/** How often to re-ask the server while waiting for the row to appear. */
const POLL_MS = 1200;

/**
 * The dock's own queue query key (`EnrollQueueContext`'s `QUEUE_KEY`). Duplicated
 * rather than exported from there to avoid importing the provider into a hook the
 * provider's own child uses — if the two ever drift, this poll silently stops
 * refetching, which is worth the comment.
 */
const QUEUE_QUERY_KEY = ['flow-queue'] as const;

/**
 * Subscribes the dock to "please open" requests from elsewhere in the app
 * (`lib/enroll-queue-open-request.ts`) — today: approving a signal draft, which
 * loads the queue with the copy pre-filled.
 *
 * It opens the queue tab AND expands the row the approval created, because a
 * collapsed row hides the entire point: the pre-filled per-step copy. The row is
 * identified as "the first item to appear with an id higher than any we had when
 * the request arrived" — the client is never told the item id (the artifact wire
 * shape carries no contact or queue reference), and the newest id is unambiguous
 * for a request that fires within milliseconds of the insert.
 *
 * Expanding reuses `requestQueueFlash(contactId, { openEditorAfterAssign: true })`,
 * the mechanism the Browse-pick follow-up already added for landing a rep on an
 * already-expanded row — rather than a second, parallel way to do the same thing.
 *
 * ITS OWN HOOK because this needed tests: two earlier bugs here were invisible to
 * tsc (a missing import that crashed the dock, and `open()` silently resolving to
 * `window.open`), and a 900-line component is not a testable unit.
 */
export function useEnrollQueueOpenRequests(
  openQueueTab: () => void,
  items: OpenRequestTarget[],
  requestQueueFlash?: (contactId: string, opts?: { openEditorAfterAssign?: boolean }) => void,
): string | null {
  /** Highest id seen when the request arrived; null when nothing is being watched. */
  const [baselineId, setBaselineId] = useState<number | null>(null);
  /**
   * The row to expand, returned so the caller can pass it DOWN as a prop.
   *
   * `requestQueueFlash` alone was not enough: it is a one-shot request that a
   * sibling effect consumes as soon as it has scrolled, so whether the row's own
   * effect observes it first is a race — it won in every scenario I could drive
   * headlessly and lost in the real browser. Durable state cannot lose that race.
   */
  const [focusContactId, setFocusContactId] = useState<string | null>(null);
  const watchStartedAt = useRef(0);
  // Read inside the subscription without making it a dependency — resubscribing on
  // every queue change would drop a request that lands mid-swap.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const flashRef = useRef(requestQueueFlash);
  flashRef.current = requestQueueFlash;
  const queryClient = useQueryClient();

  const onRequest = useCallback(() => {
    openQueueTab();
    /*
     * REFETCH THE QUEUE, REPEATEDLY, UNTIL THE ROW SHOWS UP.
     *
     * Two things make a single refetch wrong. The approval's response only
     * invalidates the ARTIFACT queries, so an already-mounted dock keeps showing
     * data from before the row existed — that is why this looked like it worked
     * when the dock was opened fresh (mount fetches) and not when it was already
     * open. And the action is ASYNCHRONOUS: the POST answers `202 pending
     * dispatch` and the handler creates the row afterwards, so an immediate
     * refetch races the insert and comes back 304 — which is exactly what the
     * server log showed.
     *
     * So the watcher below polls until the row appears or the window closes.
     */
    /*
     * Flash the newest row we ALREADY know about, then keep watching for a newer
     * one to supersede it.
     *
     * Both orders happen and neither is predictable: the approval's own
     * invalidate can land the refetch before this callback runs (in which case the
     * row is already here and waiting for a "newer" id would wait forever — the
     * bug this replaces), or after (in which case the newest known row is the
     * previous one and the watcher corrects it a beat later). Nothing else creates
     * rows in that window, so "newest" is the right target either way.
     */
    const newest = itemsRef.current.reduce<OpenRequestTarget | null>(
      (best, i) => (!best || i.id > best.id ? i : best),
      null,
    );
    watchStartedAt.current = Date.now();
    setBaselineId(newest?.id ?? 0);
    if (newest) {
      setFocusContactId(newest.contact_id);
      // Still requested: the flash is what SCROLLS the row into view and tints it.
      flashRef.current?.(newest.contact_id, { openEditorAfterAssign: true });
    }
  }, [openQueueTab]);

  useEffect(() => enrollQueueOpenRequest.subscribe(onRequest), [onRequest]);

  useEffect(() => {
    if (baselineId === null) return;

    const fresh = items.filter((i) => i.id > baselineId).sort((a, b) => b.id - a.id)[0];
    if (fresh) {
      setBaselineId(null);
      setFocusContactId(fresh.contact_id);
      requestQueueFlash?.(fresh.contact_id, { openEditorAfterAssign: true });
      return;
    }

    if (Date.now() - watchStartedAt.current > WATCH_MS) {
      // Gave up: the dock is open on the queue, which is most of the value. Never
      // expand a stale row just to have expanded something.
      setBaselineId(null);
      return;
    }

    // The handler is still working (or the refetch raced the insert). Ask again.
    const timer = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: QUEUE_QUERY_KEY });
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [items, baselineId, requestQueueFlash, queryClient]);

  return focusContactId;
}
